// Marketing landing per .impeccable/shapes/marketing-landing.md.
//
// Seven sections, single column, generous breathing space. The terracotta
// moment is the install command; everything else stays restrained per
// DESIGN.md. No card grids, no gradient text, no hero-metric template, no
// side-stripe borders.

import { Link } from 'react-router-dom'
import { HeroFilmstrip } from '../../components/marketing/HeroFilmstrip.jsx'
import { HowItWorksGraph } from '../../components/marketing/HowItWorksGraph.jsx'
import { InstallCommand } from '../../components/marketing/InstallCommand.jsx'
import { MarketingFooter } from '../../components/marketing/MarketingFooter.jsx'
import { MarketingHeader } from '../../components/marketing/MarketingHeader.jsx'

const SECTION_PADDING = 'mx-auto max-w-3xl px-6'
const WIDE_PADDING = 'mx-auto max-w-5xl px-6'

function SectionLabel({ children }) {
  return (
    <p className="mb-4 text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-schist)]">
      {children}
    </p>
  )
}

function NotForBullet({ children }) {
  return (
    <li className="relative pl-6 text-[var(--color-slate)]">
      <span
        aria-hidden="true"
        className="absolute left-0 top-2 inline-block h-px w-3 bg-[var(--color-schist)]"
      />
      {children}
    </li>
  )
}

export function Landing() {
  return (
    <div className="min-h-screen bg-[var(--color-limestone)]">
      <MarketingHeader />

      <main id="main-content">
        {/* Hero ----------------------------------------------------------- */}
        <section className={`${WIDE_PADDING} pt-24 pb-32 md:pt-32`}>
          <div className="flex flex-col items-start gap-8">
            <h1 className="max-w-3xl text-[clamp(2.5rem,6vw,4.5rem)] font-semibold leading-[1.05] tracking-tight text-[var(--color-basalt)]">
              Mark the path.
              <br />
              Catch the drift.
            </h1>
            <p className="max-w-2xl text-lg leading-relaxed text-[var(--color-slate)]">
              Graph-driven testing for the YAML-shaped specification of your app. Voice walkthroughs
              and direct edits become versioned flow graphs that tests are built from and drift
              detection keeps honest.
            </p>

            <InstallCommand />

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <Link
                to="/auth/signup"
                className="inline-flex items-center gap-2 rounded-md border border-[var(--color-terracotta)] bg-transparent px-4 py-2 text-sm font-medium text-[var(--color-terracotta)] hover:bg-[var(--color-terracotta)] hover:text-[var(--color-limestone)]"
              >
                Create an account
              </Link>
              <Link
                to="/docs/quickstart"
                className="text-[var(--color-slate)] underline underline-offset-4 hover:text-[var(--color-terracotta)]"
              >
                Read the docs →
              </Link>
            </div>
          </div>

          <div className="mt-20">
            <HeroFilmstrip />
          </div>
        </section>

        <hr className="mx-auto max-w-5xl border-[var(--color-sandstone)]" />

        {/* Problem -------------------------------------------------------- */}
        <section className={`${SECTION_PADDING} py-28`}>
          <SectionLabel>The problem</SectionLabel>
          <p className="max-w-2xl text-lg leading-relaxed text-[var(--color-slate)]">
            Your Playwright suite has 47 tests and you trust 12 of them. The other 35 are
            quarantined, flaky, or commented out. Selectors break silently. The QA channel fills
            with "is this drift or a real bug?" and nobody has time to find out. Tests stopped being
            a spec months ago.
          </p>
        </section>

        <hr className="mx-auto max-w-5xl border-[var(--color-sandstone)]" />

        {/* What Cairn does ------------------------------------------------- */}
        <section className={`${SECTION_PADDING} py-28`}>
          <SectionLabel>What Cairn does</SectionLabel>
          <h2 className="mb-12 text-3xl font-semibold tracking-tight text-[var(--color-basalt)]">
            A graph of your app's behavior, written down and kept honest.
          </h2>

          <div className="space-y-12">
            <div>
              <p className="mb-2 font-mono text-sm text-[var(--color-terracotta)]">01</p>
              <h3 className="mb-2 text-xl font-semibold text-[var(--color-basalt)]">Capture</h3>
              <p className="text-[var(--color-slate)]">
                Voice walkthrough or direct YAML in your editor. The flow becomes a structured
                graph: nodes are screens, edges are user actions, assertions describe what the user
                must see for the action to count.
              </p>
            </div>
            <div>
              <p className="mb-2 font-mono text-sm text-[var(--color-terracotta)]">02</p>
              <h3 className="mb-2 text-xl font-semibold text-[var(--color-basalt)]">Version</h3>
              <p className="text-[var(--color-slate)]">
                <code className="rounded bg-[var(--color-sandstone)] px-1.5 py-0.5 font-mono text-sm">
                  .graphs/*.yaml
                </code>{' '}
                lives in your repo. Pull requests review the spec the same way they review the code.
                Tests are derived; the source of truth is the graph.
              </p>
            </div>
            <div>
              <p className="mb-2 font-mono text-sm text-[var(--color-terracotta)]">03</p>
              <h3 className="mb-2 text-xl font-semibold text-[var(--color-basalt)]">Keep honest</h3>
              <p className="text-[var(--color-slate)]">
                <code className="rounded bg-[var(--color-sandstone)] px-1.5 py-0.5 font-mono text-sm">
                  cairn scan
                </code>{' '}
                detects when the code and the graph diverge. Confidence-scored patches are proposed
                inline; you accept or dismiss them. The graph and the app stay aligned without daily
                archaeology.
              </p>
            </div>
          </div>
        </section>

        {/* How it works --------------------------------------------------- */}
        <section className="bg-[var(--color-sandstone)]/40 py-28">
          <div className={WIDE_PADDING}>
            <SectionLabel>How it works</SectionLabel>
            <h2 className="mb-12 max-w-2xl text-3xl font-semibold tracking-tight text-[var(--color-basalt)]">
              Speak the flow → commit the YAML → tests + drift detection.
            </h2>
            <HowItWorksGraph />
            <p className="mt-6 max-w-2xl text-sm text-[var(--color-schist)]">
              A sample login flow rendered from{' '}
              <code className="font-mono">.graphs/login.yaml</code>. The same view appears in the
              dashboard.
            </p>
          </div>
        </section>

        {/* Who it's for --------------------------------------------------- */}
        <section className={`${SECTION_PADDING} py-28`}>
          <SectionLabel>Who it's for</SectionLabel>
          <div className="space-y-6 text-[var(--color-slate)]">
            <p>
              <strong className="text-[var(--color-basalt)]">Solo and technical founders</strong>{' '}
              shipping an app without a QA team. You want a spec your tests live up to, not a suite
              that slowly rots.
            </p>
            <p>
              <strong className="text-[var(--color-basalt)]">
                Mid-stage startup engineers (5–30 eng, no dedicated QA)
              </strong>{' '}
              who write their own integration tests and feel the maintenance gravity pulling the
              suite away from reality.
            </p>
          </div>

          <div className="mt-12">
            <p className="mb-3 text-sm font-medium uppercase tracking-wider text-[var(--color-schist)]">
              Not for
            </p>
            <ul className="space-y-2 text-sm">
              <NotForBullet>
                Teams with a dedicated QA organization and a mature Cypress / Playwright suite
                they're happy with
              </NotForBullet>
              <NotForBullet>Tooling-skeptical engineers who prefer hand-written tests</NotForBullet>
              <NotForBullet>No-code or low-code app builders without a real git repo</NotForBullet>
            </ul>
          </div>
        </section>

        <hr className="mx-auto max-w-5xl border-[var(--color-sandstone)]" />

        {/* Read more ------------------------------------------------------ */}
        <section className={`${SECTION_PADDING} py-28`}>
          <SectionLabel>Read more</SectionLabel>
          <ul className="space-y-3 text-lg">
            <li>
              <Link
                to="/docs/quickstart"
                className="text-[var(--color-terracotta)] underline underline-offset-4 hover:text-[var(--color-terracotta-deep)]"
              >
                Quickstart →
              </Link>
            </li>
            <li>
              <Link
                to="/docs/graph-driven-testing"
                className="text-[var(--color-terracotta)] underline underline-offset-4 hover:text-[var(--color-terracotta-deep)]"
              >
                Graph-driven testing: a 5-minute concept →
              </Link>
            </li>
          </ul>
          <p className="mt-10 max-w-2xl text-xs italic text-[var(--color-schist)]">
            Built on graph-coverage criteria from Ammann &amp; Offutt,{' '}
            <em>Introduction to Software Testing</em>, Ch. 7.
          </p>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
